// Colours, labels and the bucket bar of the Experiments v2 page.
import type {
  ExpV2Counts,
  ExpV2EndReason,
  ExpV2Layer,
  ExpV2PreRegistration,
  ExpV2Status,
  ExpV2Targeting,
  ExpV2Test,
} from '@/types/experimentsV2';
import { BUCKETS } from './units';

export const STATUS_COLOR: Record<ExpV2Status, string> = {
  draft: 'default',
  scheduled: 'blue',
  running: 'green',
  paused: 'orange',
  ended: 'default',
  rolled_out: 'purple',
};

export const STATUS_LABEL: Record<ExpV2Status, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  running: 'Running',
  paused: 'Paused',
  ended: 'Ended',
  rolled_out: 'Rolled out',
};

export const END_REASON_LABEL: Record<ExpV2EndReason, string> = {
  test_ended: 'Test ended',
  test_paused: 'Test paused',
  variant_removed: 'Variant removed',
  winner_rolled_out: 'Winner rolled out',
  qa_forced: 'QA forced',
};

/** The split (SRM) check as a badge; red at p < 0.001 (the server sets `flagged`). */
export function srmBadge(c: ExpV2Counts): { color: 'red' | 'green' | 'default'; text: string } {
  const assigned = c.variants.reduce((n, v) => n + v.assigned, 0);
  if (assigned === 0) return { color: 'default', text: 'No players yet' };
  if (c.srm.flagged) return { color: 'red', text: `Split off (p=${c.srm.pValue.toPrecision(4)})` };
  return { color: 'green', text: 'Split OK' };
}

export interface LayerSegment {
  key: string;
  start: number;
  size: number;
  /** Buckets serving players now (the live part of the reserved range). */
  live: number;
  status: ExpV2Status | 'free';
}

/** The layer's 1000 buckets as test ranges and free gaps, in bucket order. */
export function layerSegments(layer: ExpV2Layer): LayerSegment[] {
  const out: LayerSegment[] = [];
  let at = 0;
  for (const r of [...layer.ranges].sort((a, b) => a.rangeStart - b.rangeStart)) {
    if (r.rangeStart > at) {
      out.push({ key: '', start: at, size: r.rangeStart - at, live: 0, status: 'free' });
    }
    out.push({
      key: r.key,
      start: r.rangeStart,
      size: r.rangeSize,
      live: Math.min(r.rolloutSize, r.rangeSize),
      status: r.status,
    });
    at = r.rangeStart + r.rangeSize;
  }
  if (at < BUCKETS) out.push({ key: '', start: at, size: BUCKETS - at, live: 0, status: 'free' });
  return out;
}

const span = (r: { min?: number; max?: number }) =>
  r.max === undefined
    ? `${r.min ?? 0} or more`
    : r.min === undefined
      ? `up to ${r.max}`
      : `${r.min} to ${r.max}`;

/** One line per targeting rule, for the read-only summary. */
export function describeTargeting(t: ExpV2Targeting | null | undefined): string[] {
  if (!t) return ['Everyone'];
  const lines: string[] = [];
  if (t.platforms?.length) lines.push(`Platforms: ${t.platforms.join(', ')}`);
  if (t.countries?.include?.length) lines.push(`Countries: ${t.countries.include.join(', ')}`);
  if (t.countries?.exclude?.length) lines.push(`Not in: ${t.countries.exclude.join(', ')}`);
  if (t.payer) lines.push(`Payer: ${t.payer === 'free' ? 'never paid' : 'paid at least once'}`);
  if (t.daysSinceInstall) lines.push(`Days since install: ${span(t.daysSinceInstall)}`);
  if (t.level) lines.push(`Level: ${span(t.level)}`);
  if (t.minAppVersion) lines.push(`App version ${t.minAppVersion} or newer`);
  if (t.userListId) lines.push(`User list ${t.userListId}`);
  return lines.length ? lines : ['Everyone'];
}

/** The version gate: builds older than this are never assigned. */
export function describeGate(t: ExpV2Test): string[] {
  const lines: string[] = [];
  if (t.requiredAppVersion) lines.push(`App version ${t.requiredAppVersion} or newer`);
  if (t.requiredBuildNumbers?.android !== undefined)
    lines.push(`Android build ${t.requiredBuildNumbers.android} or newer`);
  if (t.requiredBuildNumbers?.ios !== undefined)
    lines.push(`iOS build ${t.requiredBuildNumbers.ios} or newer`);
  return lines.length ? lines : ['No version gate'];
}

export interface CountShareRow {
  variant: string;
  assigned: number;
  open: number;
  exposed: number;
  /** Percent of all assigned players, one decimal. */
  share: number;
  /** Planned weight in percent. */
  planned: number;
}

export function countShares(t: ExpV2Test, c: ExpV2Counts): CountShareRow[] {
  const total = c.variants.reduce((n, v) => n + v.assigned, 0);
  return c.variants.map((v) => ({
    ...v,
    share: total === 0 ? 0 : Math.round((v.assigned / total) * 1000) / 10,
    planned: (t.variants.find((x) => x.key === v.variant)?.weight ?? 0) / 10,
  }));
}

/** `YYYY-MM-DD HH:mm` in IST, the time zone the team reads. */
export function istTime(iso: string): string {
  return new Date(iso)
    .toLocaleString('sv-SE', { timeZone: 'Asia/Kolkata', hour12: false })
    .slice(0, 16);
}

const orNotSet = (v: unknown) =>
  v === undefined || v === null || v === '' ? 'not set' : String(v);

/** A draft may be saved with only some pre-registration fields; missing ones read "not set". */
export function describePreRegistration(pre: Partial<ExpV2PreRegistration>): string {
  const guardrails = pre.guardrails?.length ? pre.guardrails.join(', ') : 'not set';
  return (
    `${orNotSet(pre.hypothesis)} (primary ${orNotSet(pre.primaryMetric)}, guardrails ${guardrails}, ` +
    `MDE ${orNotSet(pre.minDetectableEffect)}, planned end ${orNotSet(pre.plannedEndDate)})`
  );
}
